<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useBranding } from 'shared/composables/useBranding';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseBoolean } from '@chatwoot/utils';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import FontSize from './FontSize.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AccessToken from './AccessToken.vue';
import MfaSettingsCard from './MfaSettingsCard.vue';
import ActiveSessions from './ActiveSessions.vue';
import Policy from 'dashboard/components/policy.vue';
import SettingsLayout from '../SettingsLayout.vue';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-next/polaris/ui/tabs';
import {
  RadioGroup,
  RadioGroupItem,
} from 'dashboard/components-next/polaris/ui/radio-group';
import { Label as KitLabel } from 'dashboard/components-next/polaris/ui/label';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export default {
  components: {
    MessageSignature,
    SectionLayout,
    FontSize,
    UserLanguageSelect,
    UserProfilePicture,
    Policy,
    UserBasicDetails,
    SettingsLayout,
    Tabs,
    TabsList,
    TabsTrigger,
    RadioGroup,
    RadioGroupItem,
    KitLabel,
    ChangePassword,
    NotificationPreferences,
    AudioNotifications,
    AccessToken,
    MfaSettingsCard,
    ActiveSessions,
    BaseSettingsHeader,
  },
  setup() {
    const { isEditorHotKeyEnabled, updateUISettings } = useUISettings();
    const { currentFontSize, updateFontSize } = useFontSize();
    const { replaceInstallationName } = useBranding();

    return {
      currentFontSize,
      updateFontSize,
      isEditorHotKeyEnabled,
      updateUISettings,
      replaceInstallationName,
    };
  },
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      name: '',
      displayName: '',
      email: '',
      messageSignature: '',
      hotKeys: [
        {
          key: 'enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-enter.svg',
          darkImage: '/assets/images/dashboard/profile/hot-key-enter-dark.svg',
        },
        {
          key: 'cmd_enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter.svg',
          darkImage:
            '/assets/images/dashboard/profile/hot-key-ctrl-enter-dark.svg',
        },
      ],
      notificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      audioNotificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    apiAndWebhooksEnabled() {
      if (!this.isOnChatwootCloud) return true;

      return this.currentUser.accounts.some(
        account => account.api_and_webhooks
      );
    },
    accessTokenDescription() {
      if (!this.apiAndWebhooksEnabled) {
        return this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.PAID_PLAN_NOTE');
      }

      return this.replaceInstallationName(
        this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE')
      );
    },
    isMfaEnabled() {
      return parseBoolean(window.chatwootConfig?.isMfaEnabled);
    },
    // R2-9-7 (phase 9.5): four tabs on the same page and route, the open one in the address (?tab=), so a link
    // (the missing-signature alert, the 2FA page) opens the right one.
    tabs() {
      return [
        {
          value: 'details',
          label: this.$t('SETTINGS_PIECES.PROFILE_TABS.DETAILS'),
        },
        {
          value: 'security',
          label: this.$t('SETTINGS_PIECES.PROFILE_TABS.SECURITY'),
        },
        {
          value: 'notifications',
          label: this.$t('SETTINGS_PIECES.PROFILE_TABS.NOTIFICATIONS'),
        },
        {
          value: 'access-token',
          label: this.$t('SETTINGS_PIECES.PROFILE_TABS.ACCESS_TOKEN'),
        },
      ];
    },
    activeTab() {
      const { tab } = this.$route.query;
      return this.tabs.some(item => item.value === tab) ? tab : 'details';
    },
    sendKey() {
      return this.hotKeys.find(hotKey => this.isEditorHotKeyEnabled(hotKey.key))
        ?.key;
    },
  },
  mounted() {
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.displayName = this.currentUser.display_name;
      this.messageSignature = this.currentUser.message_signature;
    },
    async dispatchUpdate(payload, successMessage, errorMessage) {
      let alertMessage = '';
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;

        return true; // return the value so that the status can be known
      } catch (error) {
        alertMessage = parseAPIErrorResponse(error) || errorMessage;

        return false; // return the value so that the status can be known
      } finally {
        useAlert(alertMessage);
      }
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName } = userAttributes;
      const hasEmailChanged = this.currentUser.email !== email;
      this.name = name || this.name;
      this.email = email || this.email;
      this.displayName = displayName || this.displayName;

      const updatePayload = {
        name: this.name,
        email: this.email,
        displayName: this.displayName,
        avatar: this.avatarFile,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
      );

      if (hasEmailChanged && success) clearCookiesOnLogout();
    },
    async updateSignature(signature) {
      const payload = { message_signature: signature };
      let successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
      );
      let errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    updateProfilePicture({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async deleteProfilePicture() {
      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        this.avatarFile = '';
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
      }
    },
    selectTab(tab) {
      this.$router.replace({ query: { ...this.$route.query, tab } });
    },
    toggleHotKey(key) {
      this.hotKeys = this.hotKeys.map(hotKey =>
        hotKey.key === key ? { ...hotKey, active: !hotKey.active } : hotKey
      );
      this.updateUISettings({ editor_message_key: key });
      useAlert(this.$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'));
    },
    async onCopyToken(value) {
      if (!this.apiAndWebhooksEnabled) return;

      await copyTextToClipboard(value);
      useAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
    },
    async resetAccessToken() {
      if (!this.apiAndWebhooksEnabled) return;

      const success = await this.$store.dispatch('resetAccessToken');
      if (success) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_SUCCESS'));
      } else {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_ERROR'));
      }
    },
  },
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader :title="$t('PROFILE_SETTINGS.TITLE')">
        <template #tabs>
          <Tabs :model-value="activeTab" @update:model-value="selectTab">
            <TabsList
              variant="line"
              :aria-label="$t('SETTINGS_PIECES.PROFILE_TABS.LABEL')"
            >
              <TabsTrigger
                v-for="tab in tabs"
                :key="tab.value"
                :value="tab.value"
              >
                {{ tab.label }}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <template v-if="activeTab === 'details'">
        <SectionLayout title="" description="">
          <div class="flex flex-col gap-6">
            <UserProfilePicture
              :src="avatarUrl"
              :name="name"
              @change="updateProfilePicture"
              @delete="deleteProfilePicture"
            />
            <UserBasicDetails
              :name="name"
              :display-name="displayName"
              :email="email"
              :email-enabled="!globalConfig.disableUserProfileUpdate"
              @update-user="updateProfile"
            />
          </div>
        </SectionLayout>
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
          :description="
            replaceInstallationName(
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
            )
          "
        >
          <FontSize
            :value="currentFontSize"
            :label="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')
            "
            :description="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
            "
            @change="updateFontSize"
          />
          <UserLanguageSelect
            :label="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.TITLE')
            "
            :description="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.NOTE')
            "
          />
        </SectionLayout>
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')
          "
        >
          <MessageSignature
            :message-signature="messageSignature"
            @update-signature="updateSignature"
          />
        </SectionLayout>
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
        >
          <!-- The kit's RadioCardGroup design, with the woot's light/dark pictures of each key (the shared piece
               has no picture slot yet: see "Pedidos para as peças"). Saves on change, as before. -->
          <RadioGroup
            :model-value="sendKey"
            :aria-label="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
            class="grid gap-2 sm:grid-cols-2"
            @update:model-value="toggleHotKey"
          >
            <KitLabel
              v-for="hotKey in hotKeys"
              :key="hotKey.key"
              :for="`send-key-${hotKey.key}`"
              class="grid cursor-pointer gap-3 rounded-polaris-lg border border-solid border-polaris-border p-4 font-normal transition-colors hover:bg-polaris-accent has-[[data-state=checked]]:border-polaris-foreground/40 has-[[data-state=checked]]:bg-polaris-selected has-[[data-state=checked]]:text-polaris-selected-foreground"
            >
              <span class="flex items-start gap-3">
                <RadioGroupItem
                  :id="`send-key-${hotKey.key}`"
                  :value="hotKey.key"
                  class="mt-0.5"
                />
                <span class="grid gap-0.5">
                  <span class="type-polaris-card-title">{{
                    hotKey.title
                  }}</span>
                  <span class="type-polaris-body text-polaris-muted-foreground">
                    {{ hotKey.description }}
                  </span>
                </span>
              </span>
              <img
                :src="hotKey.lightImage"
                alt=""
                class="block w-full rounded-polaris-md object-cover dark:hidden"
              />
              <img
                :src="hotKey.darkImage"
                alt=""
                class="hidden w-full rounded-polaris-md object-cover dark:block"
              />
            </KitLabel>
          </RadioGroup>
        </SectionLayout>
      </template>

      <template v-else-if="activeTab === 'security'">
        <SectionLayout
          v-if="!globalConfig.disableUserProfileUpdate"
          :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
          description=""
        >
          <ChangePassword />
        </SectionLayout>
        <SectionLayout
          v-if="isMfaEnabled"
          :title="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.NOTE')"
        >
          <MfaSettingsCard />
        </SectionLayout>
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.NOTE')"
          wide
        >
          <ActiveSessions />
        </SectionLayout>
      </template>

      <template v-else-if="activeTab === 'notifications'">
        <Policy :permissions="audioNotificationPermissions">
          <SectionLayout
            :title="
              $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')
            "
            :description="
              $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
            "
          >
            <AudioNotifications />
          </SectionLayout>
        </Policy>
        <Policy :permissions="notificationPermissions">
          <NotificationPreferences />
        </Policy>
      </template>

      <SectionLayout
        v-else
        :title="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
        :description="accessTokenDescription"
      >
        <AccessToken
          :value="currentUser.access_token"
          :disabled="!apiAndWebhooksEnabled"
          @on-copy="onCopyToken"
          @on-reset="resetAccessToken"
        />
      </SectionLayout>
    </template>
  </SettingsLayout>
</template>
